import React, { useState } from "react";
import { X, Upload, FileText, Network, Plus } from "lucide-react";
import { usePlants, useParts, usePartRouting, useTenantTypes, useIncidentCategories } from "../../api/sitroom";
import { SR_TYPES, SR_PRIORITY_STYLE } from "../../data/serviceRequestTypes";
import { SectionLabel, FieldError, inputBase } from "./wizardAtoms";

// Iter47-B — Routing suggestion banner: pulls /api/parts/{pn}/routing for every
// part in the form, dedupes suggested tenants + responsible contacts, and lets
// the user one-click add them as additional parties.
const PartRoutingSuggestions = ({ parts, setForm, form }) => {
  // Build a stable PN list (only first 3 to keep API calls bounded).
  const pns = (parts || []).slice(0, 3).map((p) => p.part_number);
  // We fetch one routing call per PN — small N, safe.
  const r1 = usePartRouting(pns[0], !!pns[0]);
  const r2 = usePartRouting(pns[1], !!pns[1]);
  const r3 = usePartRouting(pns[2], !!pns[2]);
  const routes = [r1, r2, r3].filter((q) => q.data);

  // Aggregate suggested tenants + contacts across all queried parts.
  const tenants = [];
  const contacts = [];
  const seenT = new Set();
  const seenC = new Set();
  routes.forEach((q) => {
    (q.data?.suggested_tenants || []).forEach((t) => {
      if (!seenT.has(t.id)) { seenT.add(t.id); tenants.push(t); }
    });
    (q.data?.responsible_contacts || []).forEach((c) => {
      if (!seenC.has(c.id)) { seenC.add(c.id); contacts.push(c); }
    });
  });

  // Hide self if all suggestions already appear in additionalParties.
  const linkedNames = new Set((form.additionalParties || []).map((p) => p.name));
  const newSuggestions = tenants.filter((t) => !linkedNames.has(t.name));

  if (pns.length === 0) return null;
  if (newSuggestions.length === 0 && contacts.length === 0) {
    return (
      <div className="mt-2 px-2 py-1.5 rounded" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="wizard-routing-empty">
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
          No supply-chain routing recorded for {pns.join(", ")}. Configure relationships in /admin/tenants to enable suggestions.
        </span>
      </div>
    );
  }

  const addAll = () => {
    const extras = newSuggestions.map((t) => ({
      name: t.name, role: "Suggested by routing", type: t.tenant_type_key || "",
    }));
    setForm((f) => ({ ...f, additionalParties: [...(f.additionalParties || []), ...extras] }));
  };
  const addOne = (t) => {
    setForm((f) => ({
      ...f,
      additionalParties: [...(f.additionalParties || []), { name: t.name, role: "Suggested by routing", type: t.tenant_type_key || "" }],
    }));
  };

  return (
    <div
      className="mt-2 p-2.5 rounded"
      style={{ background: "rgba(24,95,165,0.08)", border: "1px solid rgba(24,95,165,0.30)" }}
      data-testid="wizard-routing-suggestions"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Network size={11} color="#9ec3ec" />
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#9ec3ec" }}>
          Routing suggests {newSuggestions.length} parties + {contacts.length} contacts
        </span>
        {newSuggestions.length > 0 && (
          <button type="button" onClick={addAll}
            className="ml-auto px-2 py-0.5 rounded inline-flex items-center gap-1"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.4px", border: "none", cursor: "pointer", textTransform: "uppercase" }}
            data-testid="wizard-routing-add-all">
            <Plus size={9} /> Add all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {newSuggestions.map((t) => (
          <button key={t.id} type="button" onClick={() => addOne(t)}
            className="px-2 py-0.5 rounded inline-flex items-center gap-1"
            style={{ background: "rgba(78,208,132,0.10)", color: "#4ed084", border: "1px solid rgba(78,208,132,0.30)", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
            data-testid={`wizard-routing-add-${t.id}`}>
            <Plus size={8} /> {t.name}{t.tenant_type_key ? ` · ${t.tenant_type_key}` : ""}
          </button>
        ))}
      </div>
      {contacts.length > 0 && (
        <div className="mt-1.5" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#c5cad8", letterSpacing: "0.3px" }}>
          Responsible: {contacts.map((c) => `${c.name} (${c.tenant_name})`).join(", ")}
        </div>
      )}
    </div>
  );
};

// Iter47-B — Category auto-suggestion: based on linked party tenant_type_keys,
// surfaces the top incident category(ies) ranked by allowed_incident_categories
// overlap. Read-only banner; user still selects category in Step 1 / template.
const CategorySuggestion = ({ form }) => {
  const { data: tenantTypes = [] } = useTenantTypes();
  const { data: categories = [] } = useIncidentCategories();

  // Collect the tenant_type_keys from linked parties.
  const linkedKeys = new Set();
  (form.additionalParties || []).forEach((p) => {
    if (p.type) linkedKeys.add(p.type.toLowerCase());
  });
  if (linkedKeys.size === 0) return null;

  // For each category count the overlap with originating_tenant_types.
  const ranked = categories.map((c) => {
    const allowed = new Set((c.originating_tenant_types || []).map((x) => x.toLowerCase()));
    const overlap = [...linkedKeys].filter((k) => allowed.has(k)).length;
    return { ...c, overlap };
  }).filter((c) => c.overlap > 0)
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, 3);

  if (ranked.length === 0) return null;

  return (
    <div
      className="mt-3 p-2.5 rounded"
      style={{ background: "rgba(243,177,74,0.08)", border: "1px solid rgba(243,177,74,0.30)" }}
      data-testid="wizard-category-suggestion"
    >
      <div className="mb-1" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#f3b14a" }}>
        Suggested incident categories
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ranked.map((c) => (
          <span key={c.key}
            className="px-2 py-0.5 rounded inline-flex items-center gap-1"
            style={{ background: "rgba(243,177,74,0.12)", color: "#f3b14a", border: "1px solid rgba(243,177,74,0.35)", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 600 }}
            data-testid={`wizard-category-suggestion-${c.key}`}>
            {c.names?.en || c.key} · {c.default_severity?.toUpperCase()}
          </span>
        ))}
      </div>
      <div className="mt-1.5" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#c5cad8", letterSpacing: "0.3px" }}>
        Based on linked party tenant types: {[...linkedKeys].join(", ")}.
      </div>
    </div>
  );
};

const PartTagInput = ({ value, onChange }) => {
  const [input, setInput] = useState("");
  const { data: matches = [] } = useParts(input);
  const filtered = (matches || []).filter(
    (m) => !(value || []).some((p) => p.part_number === m.part_number)
  );

  const add = (part) => {
    onChange([...(value || []), part]);
    setInput("");
  };
  const remove = (pn) => onChange((value || []).filter((p) => p.part_number !== pn));

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="wizard-parts-tags">
        {(value || []).map((p) => (
          <span
            key={p.part_number}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
            style={{
              background: "rgba(24,95,165,0.16)",
              border: "1px solid rgba(24,95,165,0.45)",
              color: "#9ec3ec",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
            }}
          >
            {p.part_number}
            <button
              type="button"
              onClick={() => remove(p.part_number)}
              style={{ background: "none", border: "none", color: "#9ec3ec", cursor: "pointer", padding: 0 }}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[0]) add(filtered[0]);
          }
        }}
        placeholder="Type part number or name…"
        style={inputBase}
        data-testid="wizard-parts-input"
      />
      {input && filtered.length > 0 && (
        <div
          className="mt-1 rounded overflow-hidden"
          style={{
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.10)",
            maxHeight: 180,
            overflowY: "auto",
          }}
        >
          {filtered.slice(0, 6).map((m) => (
            <button
              key={m.part_number}
              type="button"
              onClick={() => add(m)}
              className="w-full text-left px-2.5 py-1.5 transition-colors"
              style={{
                background: "transparent",
                border: "none",
                color: "#c5cad8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(24,95,165,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              data-testid={`wizard-part-suggestion-${m.part_number}`}
            >
              <span style={{ color: "#9ec3ec" }}>{m.part_number}</span>{" "}
              <span style={{ color: "#9198b0" }}>· {m.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AttachmentDrop = ({ files, onChange }) => {
  const [dragOver, setDragOver] = useState(false);
  const handle = (newOnes) => {
    const incoming = Array.from(newOnes);
    const valid = incoming.filter((f) => f.size <= 25 * 1024 * 1024);
    const merged = [...(files || []), ...valid].slice(0, 5);
    onChange(merged);
  };
  return (
    <div data-testid="wizard-attachments">
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handle(e.dataTransfer.files);
        }}
        className="flex items-center justify-center transition-colors cursor-pointer"
        style={{
          background: dragOver ? "rgba(24,95,165,0.08)" : "#0d0f18",
          border: `1.5px dashed ${dragOver ? "#185FA5" : "rgba(255,255,255,0.15)"}`,
          borderRadius: 6,
          padding: "18px 12px",
          textAlign: "center",
        }}
      >
        <input
          type="file"
          multiple
          accept="image/*,.pdf,.csv,.txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx"
          style={{ display: "none" }}
          onChange={(e) => handle(e.target.files)}
          data-testid="wizard-file-input"
        />
        <div className="flex flex-col items-center gap-1">
          <Upload size={18} style={{ color: "#9ec3ec" }} />
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#c5cad8",
              letterSpacing: "0.5px",
            }}
          >
            Drop photos, delivery notes, quality reports, CMM data
          </div>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              color: "#6b7388",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            or click to browse · {(files || []).length}/5
          </div>
        </div>
      </label>
      {(files || []).length > 0 && (
        <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
          {files.map((f, i) => {
            const isImg = f.type && f.type.startsWith("image/");
            const url = isImg ? URL.createObjectURL(f) : null;
            return (
              <div
                key={i}
                className="rounded overflow-hidden relative"
                style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
                data-testid={`wizard-file-thumb-${i}`}
              >
                {isImg ? (
                  <img src={url} alt={f.name} style={{ width: "100%", height: 80, objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FileText size={22} color="#9ec3ec" />
                  </div>
                )}
                <div
                  className="px-1.5 py-1"
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    color: "#c5cad8",
                    letterSpacing: "0.3px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={f.name}
                >
                  {f.name}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onChange(files.filter((_, j) => j !== i));
                  }}
                  className="absolute top-1 right-1 rounded-full"
                  style={{
                    background: "rgba(13,15,24,0.85)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    color: "#E24B4A",
                    width: 18,
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <X size={11} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WizardServiceRequestsSection = ({ form, setForm }) => {
  const [open, setOpen] = useState((form.serviceRequests || []).length > 0);
  const list = form.serviceRequests || [];

  const toggleType = (typeId) => {
    const exists = list.find((s) => s.type === typeId);
    if (exists) {
      setForm((f) => ({
        ...f,
        serviceRequests: (f.serviceRequests || []).filter((s) => s.type !== typeId),
      }));
    } else {
      const meta = SR_TYPES.find((t) => t.id === typeId);
      setForm((f) => ({
        ...f,
        serviceRequests: [
          ...(f.serviceRequests || []),
          {
            type: typeId,
            title: `${meta?.labelEn || typeId} — `,
            priority: "medium",
            due_date: "",
            description: "",
            assignee_name: "",
          },
        ],
      }));
    }
  };

  const updateEntry = (typeId, key, value) => {
    setForm((f) => ({
      ...f,
      serviceRequests: (f.serviceRequests || []).map((s) =>
        s.type === typeId ? { ...s, [key]: value } : s,
      ),
    }));
  };

  const selectedIds = new Set(list.map((s) => s.type));

  return (
    <div className="mt-6" data-testid="wizard-sr-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-md"
        style={{
          background: open ? "rgba(24,95,165,0.10)" : "transparent",
          border: "1px dashed rgba(24,95,165,0.45)",
          color: "#9ec3ec",
          fontFamily: "DM Mono, monospace",
          fontSize: 11,
          letterSpacing: "0.5px",
        }}
        data-testid="wizard-sr-toggle"
      >
        {open ? "▾" : "＋"} Service Requests (optional)
        {list.length > 0 && (
          <span
            className="ml-1"
            style={{
              background: "rgba(24,95,165,0.30)",
              color: "#cfe1f6",
              padding: "1px 6px",
              borderRadius: 3,
              fontSize: 9,
              letterSpacing: "0.5px",
            }}
          >
            {list.length}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {SR_TYPES.map((t) => {
              const Icon = t.Icon;
              const active = selectedIds.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleType(t.id)}
                  className="flex flex-col items-center gap-1.5 rounded-md p-2"
                  style={{
                    background: active ? `${t.color}1f` : "#08090d",
                    border: `1px solid ${active ? t.color : "rgba(255,255,255,0.08)"}`,
                    color: active ? t.color : "#cfd1d8",
                    cursor: "pointer",
                  }}
                  data-testid={`wizard-sr-toggle-${t.id}`}
                  aria-pressed={active}
                >
                  <Icon size={14} />
                  <span
                    style={{
                      fontFamily: "DM Mono, monospace",
                      fontSize: 9,
                      letterSpacing: "0.4px",
                      lineHeight: 1.2,
                      textAlign: "center",
                    }}
                  >
                    {t.labelEn}
                  </span>
                </button>
              );
            })}
          </div>

          {list.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="wizard-sr-entries">
              {list.map((sr) => {
                const meta = SR_TYPES.find((t) => t.id === sr.type);
                return (
                  <div
                    key={sr.type}
                    className="rounded-md p-3"
                    style={{
                      background: "#0d0f18",
                      border: `1px solid ${meta?.color || "#9aa1b3"}55`,
                    }}
                    data-testid={`wizard-sr-entry-${sr.type}`}
                  >
                    <div
                      className="mb-2"
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 10,
                        letterSpacing: "1px",
                        color: meta?.color || "#9aa1b3",
                        textTransform: "uppercase",
                      }}
                    >
                      {meta?.labelEn || sr.type}
                    </div>
                    <input
                      type="text"
                      value={sr.title}
                      onChange={(e) => updateEntry(sr.type, "title", e.target.value)}
                      placeholder="Title"
                      style={{ ...inputBase, marginBottom: 6 }}
                      data-testid={`wizard-sr-title-${sr.type}`}
                    />
                    <div className="grid sm:grid-cols-3 gap-2 mb-2">
                      <input
                        type="text"
                        value={sr.assignee_name}
                        onChange={(e) => updateEntry(sr.type, "assignee_name", e.target.value)}
                        placeholder="Assignee (name or tenant)"
                        style={inputBase}
                        data-testid={`wizard-sr-assignee-${sr.type}`}
                      />
                      <select
                        value={sr.priority}
                        onChange={(e) => updateEntry(sr.type, "priority", e.target.value)}
                        style={inputBase}
                        data-testid={`wizard-sr-priority-${sr.type}`}
                      >
                        {Object.entries(SR_PRIORITY_STYLE).map(([id, p]) => (
                          <option key={id} value={id} style={{ background: "#0d0f18" }}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="datetime-local"
                        value={sr.due_date}
                        onChange={(e) => updateEntry(sr.type, "due_date", e.target.value)}
                        style={inputBase}
                        data-testid={`wizard-sr-due-${sr.type}`}
                      />
                    </div>
                    <textarea
                      value={sr.description}
                      onChange={(e) => updateEntry(sr.type, "description", e.target.value)}
                      placeholder="Description (optional)"
                      rows={2}
                      style={{ ...inputBase, resize: "vertical" }}
                      data-testid={`wizard-sr-description-${sr.type}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const WizardStep2Details = ({ form, setForm, errors }) => {
  const { data: plants = [] } = usePlants();
  const counts = { title: form.title.length, desc: form.description.length };

  return (
    <div data-testid="wizard-step2">
      <h2
        style={{
          fontFamily: "Syne, sans-serif",
          fontSize: 22,
          fontWeight: 600,
          color: "#f0f2f8",
          marginBottom: 24,
          letterSpacing: "-0.3px",
        }}
      >
        Tell us what's happening
      </h2>

      <div className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <div>
          <SectionLabel>Title · {counts.title}/120</SectionLabel>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 120) }))}
            placeholder="e.g. JIS Sequence Error — Line 7 Body Shop"
            style={{
              ...inputBase,
              borderColor: errors.title ? "#E24B4A" : "rgba(255,255,255,0.10)",
            }}
            data-testid="wizard-title-input"
          />
          <FieldError>{errors.title}</FieldError>

          <div className="mt-4">
            <SectionLabel>Description · {counts.desc}/2000</SectionLabel>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 2000) }))}
              rows={5}
              placeholder="Describe the situation, what happened, when it started, and immediate actions already taken…"
              style={{
                ...inputBase,
                borderColor: errors.description ? "#E24B4A" : "rgba(255,255,255,0.10)",
                resize: "vertical",
                minHeight: 110,
              }}
              data-testid="wizard-desc-input"
            />
            <FieldError>{errors.description}</FieldError>
          </div>

          <div className="mt-4">
            <SectionLabel>Affected Part Numbers</SectionLabel>
            <PartTagInput value={form.parts} onChange={(v) => setForm((f) => ({ ...f, parts: v }))} />
            {(form.parts || []).length > 0 && (
              <PartRoutingSuggestions parts={form.parts} setForm={setForm} form={form} />
            )}
            <CategorySuggestion form={form} />
          </div>

          <div className="mt-4">
            <SectionLabel>Plant / Line</SectionLabel>
            <select
              value={form.line}
              onChange={(e) => setForm((f) => ({ ...f, line: e.target.value }))}
              style={{
                ...inputBase,
                borderColor: errors.line ? "#E24B4A" : "rgba(255,255,255,0.10)",
              }}
              data-testid="wizard-line-select"
            >
              <option value="">— Select line / area —</option>
              {plants.map((p) => (
                <option key={p.line} value={`${p.plant} · ${p.line}`}>
                  {p.plant} · {p.line} ({p.type})
                </option>
              ))}
            </select>
            <FieldError>{errors.line}</FieldError>
          </div>
        </div>

        <div>
          <SectionLabel>Estimated production impact</SectionLabel>
          <div className="flex gap-2">
            <div className="flex-1">
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  color: "#6b7388",
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Units affected
              </div>
              <input
                type="number"
                min="0"
                value={form.units}
                onChange={(e) => setForm((f) => ({ ...f, units: e.target.value }))}
                placeholder="0"
                style={inputBase}
                data-testid="wizard-units-input"
              />
            </div>
            <div className="flex-1">
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  color: "#6b7388",
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Hours affected
              </div>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.hours}
                onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                placeholder="0"
                style={inputBase}
                data-testid="wizard-hours-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <SectionLabel>Estimated EUR loss</SectionLabel>
            <div className="relative">
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 13,
                  color: "#9198b0",
                }}
              >
                €
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={form.loss}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d]/g, "");
                  const formatted = raw ? Number(raw).toLocaleString("de-DE") : "";
                  setForm((f) => ({ ...f, loss: formatted }));
                }}
                placeholder="0"
                style={{ ...inputBase, paddingLeft: 22 }}
                data-testid="wizard-loss-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <SectionLabel>Attachments · max 5 files, 25MB each</SectionLabel>
            <AttachmentDrop
              files={form.files}
              onChange={(fs) => setForm((f) => ({ ...f, files: fs }))}
            />
          </div>

          <div className="mt-4">
            <SectionLabel>Internal reference (optional)</SectionLabel>
            <input
              type="text"
              value={form.internalRef}
              onChange={(e) => setForm((f) => ({ ...f, internalRef: e.target.value }))}
              placeholder="Complaint #, delivery note, customer ref…"
              style={inputBase}
              data-testid="wizard-internalref-input"
            />
          </div>

          <WizardServiceRequestsSection form={form} setForm={setForm} />
        </div>
      </div>
    </div>
  );
};
